import React from 'react';
import { bool, object, string } from 'prop-types';
import classNames from 'classnames';
import {
  dateIsAfter,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
} from '../../util/dates';
import { FieldSingleDatePicker } from '../../components';

import css from './FieldDatesInput.module.css';

// The form stores the time of day in the listing's time zone; the picker works in local time.
const formatInTimeZone = timeZone => v =>
  v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : { date: null };
const parseInTimeZone = timeZone => v =>
  v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : { date: null };

/**
 * A start and an end date for a production's dates. The end date opens once a start date is
 * chosen, and can't be before it.
 */
const FieldDatesInput = props => {
  const {
    rootClassName,
    className,
    formId,
    startDateInputProps,
    endDateInputProps,
    values,
    timeZone,
    disabled,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const startDate = values.startDate && values.startDate.date ? values.startDate.date : null;
  const endDateDisabled = disabled || !startDate;

  return (
    <div className={classes}>
      <div className={css.formRow}>
        <div className={classNames(css.field, css.startDate)}>
          <FieldSingleDatePicker
            className={css.fieldDateInput}
            name="startDate"
            id={formId ? `${formId}.startDate` : 'startDate'}
            label={startDateInputProps.label}
            placeholderText={startDateInputProps.placeholderText}
            format={formatInTimeZone(timeZone)}
            parse={parseInTimeZone(timeZone)}
            isOutsideRange={() => false}
            useMobileMargins
            showErrorMessage={false}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={css.formRow}>
        <div className={css.field}>
          <FieldSingleDatePicker
            {...endDateInputProps}
            name="endDate"
            id={formId ? `${formId}.endDate` : 'endDate'}
            className={css.fieldDateInput}
            label={endDateInputProps.label}
            placeholderText={endDateInputProps.placeholderText}
            format={formatInTimeZone(timeZone)}
            parse={parseInTimeZone(timeZone)}
            isOutsideRange={day => !!startDate && dateIsAfter(startDate, day)}
            useMobileMargins
            showErrorMessage={false}
            disabled={endDateDisabled}
            showLabelAsDisabled={endDateDisabled}
          />
        </div>
      </div>
    </div>
  );
};

FieldDatesInput.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  startDateInputProps: null,
  endDateInputProps: null,
  timeZone: null,
  disabled: false,
};

FieldDatesInput.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  startDateInputProps: object,
  endDateInputProps: object,
  values: object.isRequired,
  timeZone: string,
  disabled: bool,
};

export default FieldDatesInput;
